<template>
  <WdsStack direction-column class="milestone-block">
    <table>
      <colgroup>
        <col v-if="milestoneContainsStatus" style="width: 30px; white-space: nowrap" />
        <col style="width: 30%" />
        <col style="width: auto" />
        <col style="width: auto" />
        <col style="width: auto" />
        <col
          v-if="milestoneBlockState === MilestoneEditState.editing"
          style="width: 30px; white-space: nowrap"
        />
      </colgroup>
      <tbody>
        <tr>
          <th v-if="milestoneContainsStatus"><!-- Room for status --></th>
          <th>
            <WdsText>
              {{ $t('Milestone name') }}
            </WdsText>
          </th>
          <th>
            <WdsText>{{ $t('PO number') }}</WdsText>
          </th>
          <th>
            <WdsText>{{ $t('Due date') }}</WdsText>
          </th>
          <th>
            <WdsText>{{ $t('Amount') }}</WdsText>
          </th>
          <th v-if="milestoneBlockState === MilestoneEditState.editing">
            <!-- Room for trashcan -->
          </th>
        </tr>
        <Milestone
          v-for="(m, index) of milestones"
          :key="m.id"
          v-model="milestones[index]"
          :milestone-block-state="milestoneBlockState"
          :show-status-column="milestoneContainsStatus"
          :currency
          :error="props.errors?.[index]"
          @delete="removeMilestone"
        />
      </tbody>
      <tfoot class="milestone-total">
        <tr class="milestone-total-spacer" aria-hidden="true">
          <td :colspan="totalColspan"></td>
        </tr>
        <tr>
          <td v-if="milestoneContainsStatus"></td>
          <td></td>
          <td></td>
          <td class="tw-text-right tw-pr-2">
            <WdsText>{{ $t('Total:') }}</WdsText>
          </td>
          <td>
            <WdsStack gap-sm align-center>
              <WdsStack direction-column>
                <WdsText>{{ currency }} {{ totalRate }}</WdsText>
                <WdsText v-if="rateChanged" strike-through tertiary>
                  {{ currency }} {{ originalRate }}
                </WdsText>
              </WdsStack>
              <WdsTooltip :text="$t('Total of all milestone amounts added.')">
                <WdsStack justify-center align-center class="tw-pt-0.5">
                  <WdsIcon weight="thin" name="help" :size="16" color="#9ca3af" />
                </WdsStack>
              </WdsTooltip>
            </WdsStack>
          </td>
          <td v-if="milestoneBlockState === MilestoneEditState.editing"></td>
        </tr>
      </tfoot>
    </table>
    <WdsStack justify-space-between align-center class="milestone-action-bar">
      <WdsButton
        v-if="milestoneBlockState === MilestoneEditState.editing"
        sm
        secondary
        data-test="add-milestone"
        @click.prevent="addMilestone"
      >
        <WdsStack justify-center align-center gap-xs>
          <WdsIcon name="plus" />
          {{ $t('Add milestone') }}
        </WdsStack>
      </WdsButton>
      <div v-else></div>
    </WdsStack>
  </WdsStack>
</template>

<script setup lang="ts">
import { computed, watch } from 'vue'
import { WdsButton, WdsIcon, WdsStack, WdsText, WdsTooltip } from '@worksome/wds-components'
import Milestone from '@/components/Milestones/Milestone.vue'
import { MilestoneEditState, MilestoneError } from '@/components/Milestones/types'
import { useTranslation } from '@/composables/translation'
import { MilestoneTypeFragment, MilestoneStatus } from '@/gql/graphql'
import { uuid } from '@/utils/tools'

const { $t } = useTranslation()

const DEFAULT_PROP_RATE = '-1'

const milestones = defineModel<MilestoneTypeFragment[]>({ required: true })

const props = withDefaults(
  defineProps<{
    milestoneBlockState: MilestoneEditState
    currency?: string
    originalRate?: string
    errors?: Array<MilestoneError>
  }>(),
  {
    currency: () => currentAccount.currency,
    originalRate: () => DEFAULT_PROP_RATE, // we can't have negatives in rates so this communicates that there is no rate
  },
)

const addMilestone = () => {
  milestones.value.push({
    id: uuid(),
    name: '',
    purchaseOrderNumber: '',
    status: MilestoneStatus.Pending,
    details: {
      id: uuid(),
      dueDate: '',
      amount: 0,
    },
  })
}

const totalRate = computed(() =>
  milestones.value.reduce((count, milestone) => {
    const milestoneDetails = milestone.details

    return (parseFloat(count) + milestoneDetails.amount || 0).toFixed(2)
  }, '0'),
)

const rateChanged = computed(
  () => props.originalRate !== DEFAULT_PROP_RATE && props.originalRate !== totalRate.value,
)

const removeMilestone = (milestone: MilestoneTypeFragment) => {
  const index = milestones.value.map(({ id }) => id).indexOf(milestone.id)
  if (index > -1) {
    milestones.value.splice(index, 1)
  }
}

const milestoneContainsStatus = computed(() =>
  milestones.value.some((milestone) => milestone.status !== MilestoneStatus.Pending),
)

const totalColspan = computed(() => {
  let cols = 4 // Base columns: Name, PO, Due date, Amount
  if (milestoneContainsStatus.value) cols += 1
  if (props.milestoneBlockState === MilestoneEditState.editing) cols += 1
  return cols
})

watch(
  milestones,
  () => {
    if (milestones.value.length === 0) {
      addMilestone() // If we ever hit an empty state we add a new clean milestone by default!
    }
  },
  { immediate: true, deep: true },
)
</script>

<style lang="scss" scoped>

.milestone-block {
  table {
    width: 100%;
    table-layout: fixed;
  }

  .milestone-action-bar {
    padding: 0px 8px;
  }

  .milestone-total {
    .milestone-total-spacer {
      td {
        padding-top: 16px;
        border-bottom: 1px solid #e5e7eb;
      }
    }

    td {
      padding-top: 16px;
    }
  }
}
</style>
